// src/pages/Categories.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const navigate = useNavigate();

  // Fetch all categories from Supabase
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, parent_id, image_url')
          .order('id');
        
        if (error) throw error;
        setCategories(data);
        
        // Set children's department as default selected
        const mainCategories = data.filter(cat => cat.parent_id === null);
        const defaultCategory = mainCategories.find(cat => cat.name === 'الأطفال') || mainCategories[0];
        
        if (defaultCategory) {
          setSelectedMainCategory(defaultCategory);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    
    fetchCategories();
  }, []);

  // Update subcategories when main category changes
  useEffect(() => {
    if (selectedMainCategory && categories.length > 0) {
      const subs = categories.filter(cat => cat.parent_id === selectedMainCategory.id);
      setSubcategories(subs);
    }
  }, [selectedMainCategory, categories]);

  // Get main categories (parent_id is null)
  const mainCategories = categories.filter(cat => cat.parent_id === null);

  // Handle main category selection
  const handleMainCategorySelect = (category) => {
    setSelectedMainCategory(category);
  };

  // Helper function to get department path
  const getDepartmentPath = (arabicName) => {
    switch (arabicName) {
      case 'الرجال':
        return 'men';
      case 'النساء':
        return 'women';
      case 'الأطفال':
        return 'children';
      default:
        return 'children';
    }
  };

  // Handle subcategory navigation
  const handleSubcategoryClick = (subcategory) => {
    const departmentPath = getDepartmentPath(selectedMainCategory.name);
    navigate(`/department/${departmentPath}/${encodeURIComponent(subcategory.name)}`);
  };

  return (
    <Box 
      sx={{ 
        minHeight: 'calc(100vh - 140px)',
        backgroundColor: '#fff',
        direction: 'rtl',
        display: 'flex'
      }}
    >
      {/* Main Categories - Right Column */}
      <Box
        sx={{
          width: '35%',
          backgroundColor: '#f8f9fa',
          borderLeft: '1px solid #e0e0e0',
          minHeight: '100vh'
        }}
      >
        
        <List sx={{ p: 0 }}>
          {mainCategories.map((category) => (
            <ListItem key={category.id} disablePadding>
              <ListItemButton
                selected={selectedMainCategory?.id === category.id}
                onClick={() => handleMainCategorySelect(category)}
                sx={{
                  py: 2,
                  px: 3,
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: selectedMainCategory?.id === category.id ? '#0f3662' : 'transparent',
                  color: selectedMainCategory?.id === category.id ? '#fff' : '#333',
                  '&:hover': {
                    backgroundColor: selectedMainCategory?.id === category.id ? '#0f3662' : '#f0f0f0',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#0f3662',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#0a2a4a',
                    }
                  }
                }}
              >
                <ListItemText 
                  primary={
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: selectedMainCategory?.id === category.id ? 'bold' : 'normal',
                        fontSize: '16px',
                        textAlign: 'right'
                      }}
                    >
                      {category.name}
                    </Typography>
                  } 
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Subcategories - Left Grid */}
      <Box
        sx={{
          width: '65%',
          p: 2,
          backgroundColor: '#fff'
        }}
      >
        {selectedMainCategory && (
          <>
            {/* Subcategories Title */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                color: '#0f3662',
                textAlign: 'center'
              }}
            >
              فئات {selectedMainCategory.name}
            </Typography>
            
            {subcategories.length > 0 ? (
              <Grid container spacing={2}>
                {subcategories.map((subcategory) => (
                  <Grid item xs={6} sm={4} md={3} key={subcategory.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        aspectRatio: '0.8',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 20px rgba(15,54,98,0.15)',
                          borderColor: '#0f3662',
                        },
                      }}
                      onClick={() => handleSubcategoryClick(subcategory)}
                    >
                      {/* Category Image */}
                      {subcategory.image_url ? (
                        <CardMedia
                          component="img"
                          sx={{
                            height: '70%',
                            objectFit: 'cover',
                            backgroundColor: '#f5f5f5'
                          }}
                          image={subcategory.image_url}
                          alt={subcategory.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      
                      {/* Fallback placeholder when no image or image fails */}
                      <Box
                        sx={{
                          height: '70%',
                          backgroundColor: '#f5f5f5',
                          display: subcategory.image_url ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed #e0e0e0'
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '32px', color: '#ccc', mb: 1 }}>
                            🏷️
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            لا توجد صورة
                          </Typography>
                        </Box>
                      </Box>

                      {/* Category Name */}
                      <CardContent 
                        sx={{ 
                          height: '30%',
                          p: 1.5,
                          '&:last-child': { pb: 1.5 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(15,54,98,0.05)'
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: '600',
                            color: '#0f3662',
                            fontSize: '14px',
                            lineHeight: 1.2,
                            textAlign: 'center',
                            wordBreak: 'break-word'
                          }}
                        >
                          {subcategory.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '300px',
                  flexDirection: 'column',
                  backgroundColor: '#f8f9fa',
                  borderRadius: 3,
                  border: '2px dashed #e0e0e0'
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <Typography 
                    sx={{ 
                      color: '#999',
                      fontSize: '32px'
                    }}
                  >
                    📦
                  </Typography>
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#666',
                    mb: 1,
                    fontWeight: 'bold'
                  }}
                >
                  لا توجد فئات فرعية
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#999',
                    textAlign: 'center',
                    maxWidth: '250px'
                  }}
                >
                  لم يتم إضافة فئات فرعية لهذا القسم بعد
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Categories;
